
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SosService from "../../services/sos.service";

// ---------------------------------
// RESPONSE HELPER
// ---------------------------------

const getResponseBody = (response) => {
  return response?.data?.status !== undefined
    ? response.data
    : response;
};

// ---------------------------------
// GET SOS ALERTS
// ---------------------------------

export const getSosAlerts = createAsyncThunk(
  "sos/getSosAlerts",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await SosService.getSosAlerts(params);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch SOS alerts"
      );
    }
  }
);

// ---------------------------------
// RESOLVE SOS
// ---------------------------------

export const resolveSos = createAsyncThunk(
  "sos/resolveSos",
  async ({ id, payload = {} }, { rejectWithValue }) => {
    try {
      const response = await SosService.resolveSos(id, payload);

      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to resolve SOS"
      );
    }
  }
);

// ---------------------------------
// CANCEL SOS
// ---------------------------------

export const cancelSos = createAsyncThunk(
  "sos/cancelSos",
  async ({ id, payload = {} }, { rejectWithValue }) => {
    try {
      const response = await SosService.cancelSos(id, payload);

      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to cancel SOS"
      );
    }
  }
);

// ---------------------------------
// INITIAL STATE
// ---------------------------------

const initialState = {
  sosAlerts: [],

  pagination: {
    total: 0,
    currentPage: 1,
    limit: 20,
    totalPages: 0,
  },

  loading: false,
  resolving: false,
  cancelling: false,

  error: null,
  successMessage: null,
};

// ---------------------------------
// SOS SLICE
// ---------------------------------

const sosSlice = createSlice({
  name: "sos",
  initialState,

  reducers: {
    clearSosError: (state) => {
      state.error = null;
    },

    clearSosMessage: (state) => {
      state.successMessage = null;
    },

    // Add SOS from Socket.IO
    addSosAlert: (state, action) => {
      const newAlert = action.payload;

      if (!newAlert) return;

      const exists = state.sosAlerts.some(
        (alert) =>
          String(alert._id) === String(newAlert._id) ||
          alert.alertId === newAlert.alertId
      );

      if (!exists) {
        state.sosAlerts.unshift(newAlert);
        state.pagination.total += 1;
      }
    },

    // Update SOS status from Socket.IO
    updateSosResolved: (state, action) => {
      const {
        id,
        alertId,
        status = "RESOLVED",
        ...updates
      } = action.payload || {};

      state.sosAlerts = state.sosAlerts.map((alert) => {
        const matches =
          (id && String(alert._id) === String(id)) ||
          (alertId && alert.alertId === alertId);

        return matches
          ? { ...alert, ...updates, status }
          : alert;
      });
    },

    // Update SOS status for any event
    updateSosStatus: (state, action) => {
      const {
        id,
        alertId,
        status,
        ...updates
      } = action.payload || {};

      state.sosAlerts = state.sosAlerts.map((alert) => {
        const matches =
          (id && String(alert._id) === String(id)) ||
          (alertId && alert.alertId === alertId);

        return matches
          ? { ...alert, ...updates, status: status || alert.status }
          : alert;
      });
    },
  },

  extraReducers: (builder) => {
    builder

      // ---------------------------------
      // GET SOS
      // ---------------------------------

      .addCase(getSosAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getSosAlerts.fulfilled, (state, action) => {
        state.loading = false;

        const response = getResponseBody(action.payload);
        const data = response?.data || {};

        state.sosAlerts = Array.isArray(data.alerts)
          ? data.alerts
          : Array.isArray(data)
            ? data
            : [];

        state.pagination = {
          total: data.pagination?.total ?? state.sosAlerts.length,
          currentPage: data.pagination?.currentPage ?? 1,
          limit: data.pagination?.limit ?? 20,
          totalPages: data.pagination?.totalPages ?? 1,
        };
      })

      .addCase(getSosAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch SOS alerts";
      })

      // ---------------------------------
      // RESOLVE SOS
      // ---------------------------------

      .addCase(resolveSos.pending, (state) => {
        state.resolving = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(resolveSos.fulfilled, (state, action) => {
        state.resolving = false;

        const { id, response: rawResponse } = action.payload;
        const response = getResponseBody(rawResponse);

        const updatedAlert =
          response?.data?.alert ||
          response?.data;

        state.sosAlerts = state.sosAlerts.map((alert) => {
          if (String(alert._id) === String(id)) {
            return updatedAlert && typeof updatedAlert === "object"
              ? { ...alert, ...updatedAlert }
              : { ...alert, status: "RESOLVED" };
          }

          return alert;
        });

        state.successMessage =
          response?.message || "SOS resolved successfully";
      })

      .addCase(resolveSos.rejected, (state, action) => {
        state.resolving = false;
        state.error = action.payload || "Failed to resolve SOS";
      })

      // ---------------------------------
      // CANCEL SOS
      // ---------------------------------

      .addCase(cancelSos.pending, (state) => {
        state.cancelling = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(cancelSos.fulfilled, (state, action) => {
        state.cancelling = false;

        const { id, response: rawResponse } = action.payload;
        const response = getResponseBody(rawResponse);

        const updatedAlert =
          response?.data?.alert ||
          response?.data;

        state.sosAlerts = state.sosAlerts.map((alert) => {
          if (String(alert._id) === String(id)) {
            return updatedAlert && typeof updatedAlert === "object"
              ? { ...alert, ...updatedAlert }
              : { ...alert, status: "CANCELLED" };
          }

          return alert;
        });

        state.successMessage =
          response?.message || "SOS cancelled successfully";
      })

      .addCase(cancelSos.rejected, (state, action) => {
        state.cancelling = false;
        state.error = action.payload || "Failed to cancel SOS";
      });
  },
});

// ---------------------------------
// EXPORT ACTIONS
// ---------------------------------

export const {
  clearSosError,
  clearSosMessage,
  addSosAlert,
  updateSosResolved,
  updateSosStatus,
} = sosSlice.actions;

export default sosSlice.reducer;
